<template>
  <div class="musubi-page musubi-page--setup">
    <header class="musubi-page__head">
      <h1>Set up your workspace</h1>
      <p class="sub">A few steps and Yorlin can start finding and contacting businesses for you.</p>
    </header>

    <div class="setup-grid">
      <!-- checklist -->
      <div class="setup-card">
        <div class="setup-head">
          <div class="sh-eyebrow">Welcome to Yorlin</div>
          <h3>Let's get your first prospects flowing</h3>
          <p v-if="status">
            <template v-if="status.required_remaining > 0">{{ status.required_remaining }} required step{{ status.required_remaining === 1 ? '' : 's' }} left before Yorlin can start.</template>
            <template v-else>All set — your workspace is ready to go.</template>
          </p>
          <div v-if="status" class="setup-prog">
            <div class="pbar"><span :style="{ width: pct + '%' }"></span></div>
            <!-- F-OB-02: was a bare "X / Y" fraction that read as the whole
                 wizard's progress; it only ever counts REQUIRED steps
                 (optional ones like teammates don't move it), so say so. -->
            <span class="pct">{{ status.required_done }} of {{ status.required_total }} required</span>
          </div>
        </div>

        <div v-if="loading" class="setup-empty">Loading…</div>
        <div v-else class="setup-list">
          <div v-for="step in status.steps" :key="step.key" class="setup-item" :class="{ next: isNext(step) }">
            <!-- F-OB-02: completed steps used to swap their ordinal for a bare
                 check, breaking the visible sequence (1, 2, ✓, 4, ✓). Keep the
                 number always present — done state layers a check badge over
                 a subdued number instead of replacing it. -->
            <div class="si-tick" :class="tickClass(step)">
              <span class="si-tick__num">{{ stepNum(step) }}</span>
              <CygnusIcon name="check" class="si-tick__check" v-if="step.done" />
            </div>
            <div class="si-body">
              <div class="si-title">
                {{ step.title }}
                <span v-if="step.required" class="req">Required</span>
                <span v-else class="optb">Optional</span>
              </div>
              <div class="si-desc" :class="{ done: step.done }">{{ step.description }}</div>

              <!-- sending-domain resolved state / set-up CTA. Reads the RESOLVED
                   domain from the tenant-safe SendingDomainResolver (via the
                   setup-status payload) — never a derived postmaster@ address and
                   never the Mailgun account sandbox (bug 2026-07-20). -->
              <div v-if="step.key === 'sending_domain' && status && status.sending_domain" class="sd-wiz">
                <template v-if="status.sending_domain.source !== 'unconfigured'">
                  <span class="sd-wiz__dom">Sending on <code>{{ status.sending_domain.domain }}</code></span>
                  <span class="sd-wiz__pill" :class="status.sending_domain.verified ? 'ok' : 'pending'">
                    {{ status.sending_domain.verified ? 'Verified' : 'Pending verification' }}
                  </span>
                  <span v-if="status.sending_domain.source === 'platform'" class="sd-wiz__note">
                    Shared platform domain — add your own to send as you.
                  </span>
                </template>
                <template v-else>
                  <span class="sd-wiz__cta">
                    <CygnusIcon name="warning" class="me-1" />No sending domain set up yet — add one so your emails send from you.
                  </span>
                </template>
              </div>

              <!-- inline company editor -->
              <div v-if="step.key === 'company' && companyOpen" class="company-editor">
                <div class="ce-row">
                  <label>Company number<input v-model.trim="company.company_number" type="text" placeholder="e.g. 12894571" /></label>
                  <label>Website<input v-model.trim="company.website" type="text" placeholder="yourcompany.co.uk" /></label>
                </div>
                <div class="ce-row">
                  <label>Sector<input v-model.trim="company.sector" type="text" placeholder="e.g. Digital agency" /></label>
                  <label>Company size
                    <select v-model="company.company_size">
                      <option value="">Choose…</option>
                      <option>Just me</option><option>2–5 people</option><option>6–20 people</option>
                      <option>21–50 people</option><option>50+ people</option>
                    </select>
                  </label>
                </div>
                <div class="ce-row ce-row--full">
                  <label>What you sell<input v-model.trim="company.sells" type="text" placeholder="e.g. bespoke joinery for homeowners" /><span class="ce-hint">Helps the AI write relevant campaigns for your offer.</span></label>
                </div>
                <div class="ce-actions">
                  <button class="btn btn-pri btn-sm" :disabled="savingCompany" @click="saveCompany">{{ savingCompany ? 'Saving…' : 'Save company details' }}</button>
                  <button class="btn btn-sec btn-sm" @click="companyOpen = false">Cancel</button>
                </div>
              </div>

              <!-- inline voice & goals editor -->
              <div v-if="step.key === 'voice' && voiceOpen" class="company-editor">
                <div class="ce-row ce-row--full">
                  <label>Tone of voice<input v-model.trim="company.tone_of_voice" type="text" placeholder="e.g. friendly, plain-English, no jargon" /><span class="ce-hint">How your campaigns should sound.</span></label>
                </div>
                <div class="ce-row ce-row--full">
                  <label>Business goals<textarea v-model.trim="company.business_goals" rows="2" placeholder="e.g. book more discovery calls with local trades"></textarea><span class="ce-hint">What the AI should steer your outreach toward.</span></label>
                </div>
                <div class="ce-actions">
                  <button class="btn btn-pri btn-sm" :disabled="savingCompany" @click="saveVoice">{{ savingCompany ? 'Saving…' : 'Save voice & goals' }}</button>
                  <button class="btn btn-sec btn-sm" @click="voiceOpen = false">Cancel</button>
                </div>
              </div>

              <!-- inline invite panel -->
              <div v-if="step.key === 'teammates' && teammatesOpen" class="invite-panel">
                <div v-if="inviteBanner" class="fbanner" :class="inviteBannerOk ? 'ok' : 'err'">{{ inviteBanner }}</div>
                <div class="ip-row">
                  <input v-model.trim="inviteEmail" type="email" placeholder="teammate@company.co.uk" />
                  <button class="btn btn-pri btn-sm" :disabled="inviting" @click="sendInvite">{{ inviting ? 'Inviting…' : 'Send invite' }}</button>
                </div>
                <div class="ip-note">Invited teammates join as <b>Campaign managers</b> (they can manage outreach &amp; capture).</div>
                <ul v-if="invites.length" class="invite-list">
                  <li v-for="inv in invites" :key="inv.id">
                    <span class="il-email">{{ inv.email }}</span>
                    <span class="il-status" :class="'st-' + inv.status">{{ inv.status }}</span>
                    <a v-if="inv.status === 'pending'" href="#" class="il-revoke" @click.prevent="revokeInvite(inv.id)">Revoke</a>
                  </li>
                </ul>
              </div>
            </div>
            <div class="si-act">
              <template v-if="step.done">
                <button v-if="step.key === 'company'" class="btn btn-sec btn-sm" @click="companyOpen = !companyOpen">Edit</button>
                <button v-else-if="step.key === 'voice'" class="btn btn-sec btn-sm" @click="voiceOpen = !voiceOpen">Edit</button>
                <span v-else class="si-doneflag"><CygnusIcon name="check" /> Done</span>
              </template>
              <template v-else>
                <a v-if="!step.required" href="#" class="si-skip" @click.prevent="skipStep(step.key)">Skip for now</a>
                <button class="btn btn-pri btn-sm" @click="stepAction(step)">{{ actionLabel(step) }}</button>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- side -->
      <aside class="setup-side">
        <div class="trial-card">
          <div class="tc-top"><CygnusIcon name="clock" /> Trial</div>
          <div class="tc-days">Sandbox trial</div>
          <div class="tc-sub">Sends go to your own addresses until you add a card</div>
          <div class="tc-note">Explore, build pipelines and preview everything during your trial. Adding a card lifts the sending cap.</div>
        </div>

        <!-- join requests (owner) -->
        <div v-if="joinRequests.length" class="side-card">
          <div class="sc-h">Join requests</div>
          <div v-if="joinBanner" class="fbanner" :class="joinBannerOk ? 'ok' : 'err'">{{ joinBanner }}</div>
          <div v-for="jr in joinRequests" :key="jr.id" class="jr-row">
            <div class="jr-who"><b>{{ jr.name || jr.email }}</b><small>{{ jr.email }}</small></div>
            <div class="jr-acts">
              <button class="btn btn-pri btn-sm" :disabled="deciding" @click="approveJoin(jr.id)">Approve</button>
              <button class="btn btn-sec btn-sm" :disabled="deciding" @click="declineJoin(jr.id)">Decline</button>
            </div>
          </div>
          <div class="sc-foot">Approving adds them as a Campaign manager and uses a user seat.</div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import useApi from '@apexl/icarus-core/src/services/api';

export default {
  name: 'SetupWizard',
  props: { apiBase: { type: String, default: '/api/v1' } },
  data() {
    return {
      api: useApi(),
      loading: true,
      status: null,
      company: { company_number: '', website: '', sector: '', company_size: '', sells: '', tone_of_voice: '', business_goals: '' },
      companyOpen: false,
      voiceOpen: false,
      savingCompany: false,
      teammatesOpen: false,
      invites: [],
      inviteEmail: '',
      inviting: false,
      inviteBanner: '',
      inviteBannerOk: false,
      joinRequests: [],
      joinBanner: '',
      joinBannerOk: false,
      deciding: false,
    };
  },
  computed: {
    pct() {
      if (!this.status || this.status.required_total === 0) return 100;
      return Math.round((this.status.required_done / this.status.required_total) * 100);
    },
  },
  mounted() {
    this.loadStatus();
    this.loadCompany();
    this.loadInvites();
    this.loadJoinRequests();
    if (String(this.$route.query.step || '') === 'company') this.companyOpen = true;
    if (String(this.$route.query.step || '') === 'voice') this.voiceOpen = true;
    if (String(this.$route.query.step || '') === 'teammates' || String(this.$route.query.tab || '') === 'requests') this.teammatesOpen = true;
  },
  methods: {
    unwrap(res) { return res?.data?.data ?? res?.data ?? res; },
    async loadStatus() {
      this.loading = true;
      try { this.status = this.unwrap(await this.api.get(this.apiBase + '/me/setup-status')); }
      catch (e) { /* leave null */ }
      finally { this.loading = false; }
    },
    async loadCompany() {
      try {
        const d = this.unwrap(await this.api.get(this.apiBase + '/me/company'));
        if (d && d.company) this.company = Object.assign(this.company, d.company);
      } catch (e) { /* ignore */ }
    },
    async loadInvites() {
      try { const d = this.unwrap(await this.api.get(this.apiBase + '/admin/users/invites')); this.invites = (d && d.invites) || []; }
      catch (e) { this.invites = []; }
    },
    async loadJoinRequests() {
      try { const d = this.unwrap(await this.api.get(this.apiBase + '/admin/join-requests')); this.joinRequests = (d && d.requests) || []; }
      catch (e) { this.joinRequests = []; }
    },
    stepNum(step) { return this.status.steps.indexOf(step) + 1; },
    isNext(step) { return !step.done && this.status && this.status.steps.filter(s => !s.done && s.required).indexOf(step) === 0; },
    tickClass(step) { return step.done ? 'done' : (this.isNext(step) ? 'next' : 'todo'); },
    actionLabel(step) {
      if (step.key === 'sending_domain') return 'Set up sending →';
      if (step.key === 'first_pipeline') return 'Start';
      if (step.key === 'teammates') return 'Invite';
      if (step.key === 'company') return 'Add details';
      if (step.key === 'voice') return 'Add voice';
      return 'Start';
    },
    stepAction(step) {
      if (step.key === 'company') { this.companyOpen = true; return; }
      if (step.key === 'voice') { this.voiceOpen = true; return; }
      if (step.key === 'teammates') { this.teammatesOpen = true; return; }
      if (step.deep_link && step.deep_link.charAt(0) === '/') { this.$router.push(step.deep_link); }
    },
    async skipStep(key) {
      try { this.status = this.unwrap(await this.api.post(this.apiBase + '/me/setup-status/skip', { step: key })); }
      catch (e) { /* ignore */ }
    },
    async saveCompany() {
      this.savingCompany = true;
      try {
        const d = this.unwrap(await this.api.post(this.apiBase + '/me/company', this.company));
        if (d && d.steps) this.status = d;
        this.companyOpen = false;
      } catch (e) { /* ignore */ }
      finally { this.savingCompany = false; }
    },
    async saveVoice() {
      this.savingCompany = true;
      try {
        const d = this.unwrap(await this.api.post(this.apiBase + '/me/company', this.company));
        if (d && d.steps) this.status = d;
        this.voiceOpen = false;
      } catch (e) { /* ignore */ }
      finally { this.savingCompany = false; }
    },
    async sendInvite() {
      this.inviteBanner = '';
      if (!this.inviteEmail) { this.inviteBanner = 'Enter an email address.'; this.inviteBannerOk = false; return; }
      this.inviting = true;
      try {
        const res = await this.api.post(this.apiBase + '/admin/users/invite', { email: this.inviteEmail }).catch(e => e?.response);
        const d = this.unwrap(res);
        if (d && d.ok) {
          this.inviteBanner = 'Invite sent to ' + this.inviteEmail + '.'; this.inviteBannerOk = true;
          this.inviteEmail = '';
          this.loadInvites();
        } else {
          this.inviteBanner = (d && d.error) || 'We couldn\'t send that invite.'; this.inviteBannerOk = false;
        }
      } catch (e) {
        this.inviteBanner = 'We couldn\'t send that invite.'; this.inviteBannerOk = false;
      } finally { this.inviting = false; }
    },
    async revokeInvite(id) {
      try { await this.api.delete(this.apiBase + '/admin/users/invites/' + id); this.loadInvites(); }
      catch (e) { /* ignore */ }
    },
    async approveJoin(id) {
      this.joinBanner = ''; this.deciding = true;
      try {
        const res = await this.api.post(this.apiBase + '/admin/join-requests/' + id + '/approve', {}).catch(e => e?.response);
        const d = this.unwrap(res);
        if (d && d.ok) { this.joinBanner = 'Approved — the teammate can now sign in.'; this.joinBannerOk = true; }
        else { this.joinBanner = (d && d.error) || 'Could not approve.'; this.joinBannerOk = false; }
        this.loadJoinRequests();
      } catch (e) { this.joinBanner = 'Could not approve.'; this.joinBannerOk = false; }
      finally { this.deciding = false; }
    },
    async declineJoin(id) {
      this.deciding = true;
      try { await this.api.post(this.apiBase + '/admin/join-requests/' + id + '/decline', {}); this.joinBanner = 'Request declined.'; this.joinBannerOk = true; this.loadJoinRequests(); }
      catch (e) { /* ignore */ }
      finally { this.deciding = false; }
    },
  },
};
</script>

<style scoped>
.musubi-page--setup { padding: 24px 28px 60px; max-width: 1080px; }
.musubi-page__head h1 { font-size: 1.5rem; letter-spacing: -.02em; }
.musubi-page__head .sub { color: var(--m-text-secondary, #697086); margin-top: 4px; }
.setup-grid { display: grid; grid-template-columns: 1fr 320px; gap: 22px; align-items: start; margin-top: 22px; }
.setup-card, .side-card, .trial-card { border-radius: var(--m-radius-lg, 14px); }
.setup-card { background: var(--m-bg-card, #fff); border: 1px solid var(--m-border, #EAECF3); box-shadow: var(--m-shadow-card); overflow: hidden; }
.setup-head { padding: 22px 24px 18px; border-bottom: 1px solid var(--m-border, #EAECF3); background: linear-gradient(180deg, var(--m-accent-soft, #EEF0FF), #fff); }
.sh-eyebrow { font-size: .68rem; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; color: var(--m-accent, #4F46E5); }
.setup-head h3 { font-size: 1.28rem; letter-spacing: -.02em; margin-top: 6px; }
.setup-head p { font-size: .85rem; color: var(--m-text-secondary, #697086); margin-top: 4px; }
.setup-prog { display: flex; align-items: center; gap: 12px; margin-top: 16px; }
.setup-prog .pbar { flex: 1; height: 8px; border-radius: 999px; background: #E4E7F5; overflow: hidden; }
.setup-prog .pbar span { display: block; height: 100%; border-radius: 999px; background: linear-gradient(90deg, var(--m-accent, #4F46E5), var(--m-accent-2, #0EA5A4)); transition: width .3s; }
.setup-prog .pct { font-size: .78rem; font-weight: 700; color: var(--m-text-secondary, #697086); font-family: var(--m-font-mono, monospace); }
.setup-list { padding: 8px 12px 14px; }
.setup-empty { padding: 30px; color: var(--m-text-tertiary, #9AA0AC); }
.setup-item { display: flex; align-items: flex-start; gap: 14px; padding: 15px 12px; border-radius: 12px; }
.setup-item + .setup-item { border-top: 1px solid #F1F5F9; }
.setup-item.next { background: var(--m-accent-soft, #EEF0FF); }
.si-tick { position: relative; width: 30px; height: 30px; border-radius: 50%; flex: none; display: flex; align-items: center; justify-content: center; font-size: .85rem; font-weight: 800; margin-top: 2px; }
.si-tick__num { line-height: 1; }
/* F-OB-02: done state keeps its ordinal (subdued) instead of replacing it, so
   the visible sequence never breaks (was 1, 2, ✓, 4, ✓ — now 1, 2, 3✓, 4, 5✓). */
.si-tick__check {
  position: absolute; right: -3px; bottom: -3px;
  width: 15px; height: 15px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: .52rem;
  background: var(--m-success, #0D9488); color: #fff;
  border: 2px solid var(--m-bg-card, #fff);
}
.si-tick.done { background: color-mix(in srgb, var(--m-success, #0D9488) 14%, #fff); color: var(--m-success, #0D9488); }
.si-tick.done .si-tick__num { opacity: .65; }
.si-tick.next { background: #fff; border: 2px solid var(--m-accent, #4F46E5); color: var(--m-accent, #4F46E5); }
.si-tick.todo { background: #fff; border: 2px solid var(--m-border, #EAECF3); color: var(--m-text-tertiary, #9AA0AC); }
.si-body { flex: 1; min-width: 0; }
.si-title { font-size: .92rem; font-weight: 700; letter-spacing: -.01em; display: flex; align-items: center; gap: 8px; }
.si-title .req { font-size: .58rem; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: var(--m-danger, #E11D48); background: #FEECF0; border-radius: 999px; padding: 1px 7px; }
.si-title .optb { font-size: .58rem; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: var(--m-text-tertiary, #9AA0AC); background: var(--m-bg-content, #F5F6FA); border-radius: 999px; padding: 1px 7px; }
.si-desc { font-size: .78rem; color: var(--m-text-tertiary, #9AA0AC); margin-top: 2px; }
.si-desc.done { color: var(--m-success, #0D9488); }
.sd-wiz { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 10px; margin-top: 8px; font-size: .78rem; }
.sd-wiz__dom code { color: var(--m-accent, #4F46E5); font-weight: 600; }
.sd-wiz__pill { font-size: .6rem; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; border-radius: 999px; padding: 2px 8px; }
.sd-wiz__pill.ok { background: color-mix(in srgb, var(--m-success, #0D9488) 14%, #fff); color: var(--m-success, #0D9488); }
.sd-wiz__pill.pending { background: #FDF3E4; color: #92400E; }
.sd-wiz__note { color: var(--m-text-tertiary, #9AA0AC); }
.sd-wiz__cta { display: inline-flex; align-items: center; color: #92400E; font-weight: 600; }
.si-act { flex: none; display: flex; align-items: center; gap: 8px; margin-top: 2px; }
.si-skip { font-size: .76rem; color: var(--m-text-tertiary, #9AA0AC); text-decoration: none; font-weight: 600; }
.si-doneflag { font-size: .76rem; color: var(--m-success, #0D9488); font-weight: 600; }
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; font: inherit; font-weight: 650; border-radius: 8px; padding: 9px 14px; cursor: pointer; border: 1px solid transparent; text-decoration: none; }
.btn-sm { padding: 7px 12px; font-size: .8rem; }
.btn-pri { background: var(--m-accent, #4F46E5); color: #fff; }
.btn-pri:hover { background: var(--m-accent-hover, #4338CA); }
.btn-sec { background: #fff; color: var(--m-text-secondary, #697086); border-color: var(--m-border, #EAECF3); }
.btn:disabled { opacity: .6; cursor: default; }
.company-editor, .invite-panel { margin-top: 12px; background: var(--m-bg-content, #F5F6FA); border: 1px solid var(--m-border, #EAECF3); border-radius: 10px; padding: 14px; }
.ce-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px; }
.ce-row--full { grid-template-columns: 1fr; }
.ce-row label, .ip-row { font-size: .72rem; font-weight: 700; color: var(--m-text-secondary, #697086); display: flex; flex-direction: column; gap: 4px; }
.ce-hint { font-size: .68rem; font-weight: 400; color: var(--m-text-tertiary, #9AA0AC); }
.company-editor input, .company-editor select, .company-editor textarea, .invite-panel input { border: 1px solid var(--m-border, #EAECF3); border-radius: 8px; padding: 8px 10px; font: inherit; font-weight: 400; }
.company-editor textarea { resize: vertical; }
.ce-actions { display: flex; gap: 8px; }
.ip-row { flex-direction: row; gap: 8px; }
.ip-row input { flex: 1; }
.ip-note { font-size: .74rem; color: var(--m-text-tertiary, #9AA0AC); margin-top: 8px; }
.invite-list { list-style: none; padding: 0; margin: 10px 0 0; }
.invite-list li { display: flex; align-items: center; gap: 10px; padding: 7px 0; border-top: 1px solid var(--m-border, #EAECF3); font-size: .8rem; }
.il-email { flex: 1; }
.il-status { font-size: .66rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; padding: 2px 8px; border-radius: 999px; background: var(--m-bg-content, #F5F6FA); color: var(--m-text-secondary, #697086); }
.il-status.st-pending { background: #FDF3E4; color: #92400E; }
.il-status.st-accepted { background: #E9FBF8; color: #0F766E; }
.il-status.st-revoked, .il-status.st-expired { background: #FEECF0; color: #9F1239; }
.il-revoke { color: var(--m-danger, #E11D48); font-weight: 600; text-decoration: none; font-size: .74rem; }
.fbanner { border-radius: 8px; padding: 9px 12px; font-size: .8rem; margin-bottom: 10px; }
.fbanner.err { background: #FEECF0; color: #9F1239; }
.fbanner.ok { background: #E9FBF8; color: #0F766E; }
.setup-side .side-card { background: var(--m-bg-card, #fff); border: 1px solid var(--m-border, #EAECF3); box-shadow: var(--m-shadow-card); padding: 18px; margin-top: 16px; }
.sc-h { font-size: .7rem; font-weight: 800; letter-spacing: .09em; text-transform: uppercase; color: var(--m-text-tertiary, #9AA0AC); margin-bottom: 12px; }
.sc-foot { font-size: .72rem; color: var(--m-text-tertiary, #9AA0AC); margin-top: 10px; }
.trial-card { background: linear-gradient(135deg, #3730A3, #4F46E5); color: #fff; padding: 18px; box-shadow: var(--m-shadow-panel); }
.tc-top { font-size: .66rem; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; }
.tc-days { font-size: 1.4rem; font-weight: 800; margin-top: 10px; }
.tc-sub { font-size: .78rem; opacity: .85; margin-top: 2px; }
.tc-note { font-size: .72rem; opacity: .78; margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(255,255,255,.2); line-height: 1.5; }
.jr-row { display: flex; align-items: center; gap: 10px; padding: 10px 0; border-top: 1px solid var(--m-border, #EAECF3); }
.jr-who { flex: 1; font-size: .82rem; }
.jr-who small { display: block; color: var(--m-text-tertiary, #9AA0AC); font-size: .72rem; }
.jr-acts { display: flex; gap: 6px; }
@media (max-width: 900px) { .setup-grid { grid-template-columns: 1fr; } }
</style>
